'use client';

import { useEffect, useState } from 'react';
import { Play, Clock, BookOpen, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface FeaturedBook {
  id: string;
  title: string;
  coverImageUrl: string | null;
  pageCount: number;
  totalDuration: number | null;
  createdAt: string;
  watermark: boolean;
}

export function FeaturedAudioBooks() {
  const router = useRouter();
  const [books, setBooks] = useState<FeaturedBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFeaturedBooks();
  }, []);

  const fetchFeaturedBooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/audio-book/featured');
      const data = await response.json();
      
      if (response.ok) {
        setBooks(data.audioBooks || []);
      } else {
        setError(data.error || 'Failed to load featured audio books');
      }
    } catch (err) {
      console.error('Error fetching featured audio books:', err);
      setError('Failed to load featured audio books');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min`;
  };

  const handleBookClick = (bookId: string) => {
    router.push(`/audio-book/${bookId}`);
  };

  if (loading) {
    return (
      <div style={{
        padding: '3rem 1.5rem',
        textAlign: 'center',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <Loader2 className="loading-spinner" size={48} style={{ margin: '0 auto' }} />
        <p style={{ marginTop: '1rem', color: 'var(--color-text-secondary)' }}>
          Loading featured stories...
        </p>
      </div>
    );
  }

  if (error || books.length === 0) {
    return null;
  }

  return (
    <>
      <style jsx>{`
        .featured-container {
          padding: 3rem 1.5rem;
          max-width: 1200px;
          margin: 0 auto;
          background: linear-gradient(135deg, rgba(168, 216, 230, 0.05) 0%, rgba(201, 182, 228, 0.05) 100%);
          border-radius: 32px;
        }

        .featured-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .featured-title {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 900;
          margin-bottom: 0.75rem;
          line-height: 1.2;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #F093FB 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .featured-subtitle {
          font-size: 1.125rem;
          color: var(--color-text-secondary);
          font-weight: 500;
        }

        .featured-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
        }

        .featured-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          border: 2px solid rgba(168, 216, 230, 0.2);
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          cursor: pointer;
        }

        .featured-card:hover {
          transform: translateY(-8px);
          border-color: rgba(168, 216, 230, 0.6);
          box-shadow: 0 12px 40px rgba(168, 216, 230, 0.3),
                      0 8px 20px rgba(201, 182, 228, 0.2);
        }

        .featured-cover {
          position: relative;
          width: 100%;
          padding-top: 140%; /* Tall aspect ratio for book covers */
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          overflow: hidden;
        }

        .featured-cover-img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .featured-card:hover .featured-cover-img {
          transform: scale(1.05);
        }

        .play-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .featured-card:hover .play-overlay {
          opacity: 1;
        }

        .play-button {
          width: 80px;
          height: 80px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          transform: scale(0.9);
          transition: transform 0.3s ease;
        }

        .featured-card:hover .play-button {
          transform: scale(1);
        }

        .featured-info {
          padding: 1.5rem;
        }

        .featured-book-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.4;
        }

        .featured-meta {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.375rem;
        }

        .featured-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #FFD700, #FFA500);
          color: white;
          font-size: 0.75rem;
          font-weight: 700;
          border-radius: 12px;
          text-transform: uppercase;
          box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
          z-index: 10;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .featured-card {
          animation: fadeInUp 0.5s ease-out;
        }

        @media (max-width: 768px) {
          .featured-title {
            font-size: 2rem;
          }

          .featured-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .featured-container {
            padding: 2rem 1rem;
          }
        }
      `}</style>

      <div className="featured-container">
        <div className="featured-header">
          <h2 className="featured-title">
            🎧 Featured Audio Books
          </h2>
          <p className="featured-subtitle">
            Discover our handpicked collection of magical stories
          </p>
        </div>

        <div className="featured-grid">
          {books.map((book, index) => (
            <div
              key={book.id}
              className="featured-card"
              onClick={() => handleBookClick(book.id)}
              style={{
                animationDelay: `${index * 0.1}s`
              }}
            >
              <div className="featured-cover">
                {book.coverImageUrl ? (
                  <img
                    src={book.coverImageUrl}
                    alt={book.title}
                    className="featured-cover-img"
                  />
                ) : (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BookOpen size={80} style={{ color: 'white', opacity: 0.5 }} />
                  </div>
                )}
                
                <div className="featured-badge">
                  ⭐ Featured
                </div>

                <div className="play-overlay">
                  <div className="play-button">
                    <Play size={32} color="#667eea" fill="#667eea" />
                  </div>
                </div>
              </div>

              <div className="featured-info">
                <div className="featured-book-title">{book.title}</div>
                
                <div className="featured-meta">
                  <div className="meta-item">
                    <BookOpen size={14} />
                    <span>{book.pageCount} pages</span>
                  </div>
                  {book.totalDuration && (
                    <div className="meta-item">
                      <Clock size={14} />
                      <span>{formatDuration(book.totalDuration)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

